import type { GridState, Generator, SimConstants } from "../sim/types.ts";
import { LAYOUTS } from "../game/levels.ts";

export interface RenderTheme {
  bg: string;
  fg: string;
  dim: string;
  accent: string;
  warn: string;
  danger: string;
  good: string;
  grid: string;
}

export const THEME: RenderTheme = {
  bg: "#0d0f12",
  fg: "#e8eaed",
  dim: "#5c6470",
  accent: "#7aa2f7",
  warn: "#e0af68",
  danger: "#f7768e",
  good: "#9ece6a",
  grid: "#1a1d23",
};

export class Renderer {
  private dpr = window.devicePixelRatio || 1;
  private w = 0;
  private h = 0;
  private freqHistory: number[] = [];
  private readonly maxHistory = 240;
  private currentLevelId = 1;

  constructor(private canvas: HTMLCanvasElement) {
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }

  setLevel(id: number): void {
    this.currentLevelId = id;
    this.freqHistory = [];
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.w = rect.width;
    this.h = rect.height;
    this.canvas.width = Math.floor(rect.width * this.dpr);
    this.canvas.height = Math.floor(rect.height * this.dpr);
  }

  render(state: GridState, c: SimConstants): void {
    this.freqHistory.push(state.frequency);
    if (this.freqHistory.length > this.maxHistory) this.freqHistory.shift();

    const ctx = this.canvas.getContext("2d")!;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);

    this.drawBackground(ctx);

    const netH = this.h * 0.65;
    this.drawNetwork(ctx, state, 0, 0, this.w, netH);
    this.drawFrequencyStrip(ctx, c, 0, netH, this.w, this.h - netH);
  }

  private drawBackground(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = THEME.bg;
    ctx.fillRect(0, 0, this.w, this.h);
    ctx.strokeStyle = THEME.grid;
    ctx.lineWidth = 1;
    const spacing = 32;
    for (let x = 0; x < this.w; x += spacing) {
      ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, this.h); ctx.stroke();
    }
    for (let y = 0; y < this.h; y += spacing) {
      ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(this.w, y + 0.5); ctx.stroke();
    }
  }

  private drawNetwork(ctx: CanvasRenderingContext2D, state: GridState, x: number, y: number, w: number, h: number): void {
    const layout = LAYOUTS[this.currentLevelId] ?? [];
    const pos = new Map<string, { px: number; py: number }>();
    for (const n of layout) pos.set(n.id, { px: x + n.x * w, py: y + n.y * h });

    const net = state.network;

    if (net) {
      // Group lines by undirected endpoint pair so we can offset parallels
      const groupKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
      const groups = new Map<string, typeof net.lines>();
      for (const l of net.lines) {
        const k = groupKey(l.from, l.to);
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k)!.push(l);
      }
      for (const [, lines] of groups) {
        const n = lines.length;
        for (let i = 0; i < n; i++) {
          const line = lines[i]!;
          const from = pos.get(line.from);
          const to = pos.get(line.to);
          if (!from || !to) continue;
          // Offset perpendicular by (i - (n-1)/2) * spacing
          const offsetIdx = i - (n - 1) / 2;
          const dx = to.px - from.px, dy = to.py - from.py;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          const nx = -dy / len, ny = dx / len;
          const off = offsetIdx * 8;
          this.drawTransmissionLine(
            ctx,
            from.px + nx * off, from.py + ny * off,
            to.px + nx * off, to.py + ny * off,
            line,
          );
        }
      }
      // Bus nodes
      for (const bus of net.buses) {
        const p = pos.get(bus.id);
        if (!p) continue;
        const isIslanded = state.islandedBuses?.includes(bus.id) ?? false;
        this.drawBus(ctx, p.px, p.py, bus.name, isIslanded);
      }
    } else {
      // Legacy: draw gen → load lines
      const loads = layout.filter((n) => n.kind === "load");
      const gens = layout.filter((n) => n.kind === "gen");
      for (const g of gens) {
        const gen = state.generators.find((x) => x.id === g.id);
        if (!gen) continue;
        const share = state.supplyMW > 0 ? gen.output / state.supplyMW : 0;
        for (const l of loads) {
          const from = pos.get(g.id)!;
          const to = pos.get(l.id)!;
          this.drawSimpleLine(ctx, from.px, from.py, to.px, to.py, gen.status === "on" ? share : 0, state.frequency);
        }
      }
    }

    // Generators
    for (const g of state.generators) {
      const p = pos.get(g.id);
      if (!p) continue;
      this.drawGenerator(ctx, p.px, p.py, g, state);
    }

    // Loads
    for (const l of state.loads) {
      const p = pos.get(l.id);
      if (!p) continue;
      let demand = l.baseDemand * (l.curve ? l.curve(state.gameMinutes) : 1);
      const isShed = l.shed === true;
      const isIslanded = net && state.islandedBuses?.includes(net.loadBus.get(l.id) ?? "") === true;
      this.drawLoad(ctx, p.px, p.py, l.name, demand, isShed, isIslanded ?? false);
    }

    // LMP overlay (layer 9)
    if (state.lmp && net) {
      for (const bus of net.buses) {
        const p = pos.get(bus.id);
        const price = state.lmp.get(bus.id);
        if (!p || price === undefined) continue;
        ctx.fillStyle = THEME.warn;
        ctx.font = "11px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(`$${price.toFixed(1)}/MWh`, p.px, p.py - 16);
      }
    }
  }

  private drawTransmissionLine(
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number, x2: number, y2: number,
    line: import("../sim/network.ts").Line,
  ): void {
    const util = line.thermalLimit > 0 ? Math.abs(line.flow) / line.thermalLimit : 0;
    const tripped = line.status === "tripped";
    let color = THEME.dim;
    if (tripped) color = THEME.danger;
    else if (util > 1) color = THEME.danger;
    else if (util > 0.7) color = THEME.warn;
    else if (util > 0.1) color = THEME.good;

    ctx.strokeStyle = tripped ? withAlpha(color, 0.4) : color;
    ctx.lineWidth = tripped ? 1.5 : 1.5 + util * 4;
    ctx.setLineDash(tripped ? [4, 6] : []);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Flow label + arrow at midpoint
    if (!tripped && Math.abs(line.flow) > 1) {
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.sqrt(dx * dx + dy * dy);
      const ux = dx / len, uy = dy / len;
      const sign = line.flow >= 0 ? 1 : -1;
      // Arrow
      const ax = mx + ux * sign * 6;
      const ay = my + uy * sign * 6;
      const bx = mx - ux * sign * 6 + uy * sign * 5;
      const by = my - uy * sign * 6 - ux * sign * 5;
      const cx = mx - ux * sign * 6 - uy * sign * 5;
      const cy = my - uy * sign * 6 + ux * sign * 5;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(cx, cy); ctx.closePath();
      ctx.fill();
      // Flow label, offset perpendicular
      const nx = -uy, ny = ux;
      ctx.fillStyle = color;
      ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        `${Math.abs(line.flow).toFixed(0)}/${line.thermalLimit} MW`,
        mx + nx * 14,
        my + ny * 14,
      );
    } else if (tripped) {
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      ctx.fillStyle = THEME.danger;
      ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("TRIPPED", mx, my - 8);
    }
  }

  private drawSimpleLine(
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number, x2: number, y2: number,
    share: number, freq: number,
  ): void {
    const alpha = 0.15 + share * 0.6;
    const color = freq < 49.6 || freq > 50.4 ? THEME.warn : THEME.dim;
    ctx.strokeStyle = withAlpha(color, alpha);
    ctx.lineWidth = 1 + share * 4;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  private drawBus(ctx: CanvasRenderingContext2D, x: number, y: number, name: string, islanded: boolean): void {
    const color = islanded ? THEME.dim : THEME.accent;
    ctx.fillStyle = withAlpha(color, 0.3);
    ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = color; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = islanded ? THEME.danger : THEME.dim;
    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(islanded ? `${name} · ISLANDED` : name, x, y + 8);
  }

  private drawGenerator(ctx: CanvasRenderingContext2D, x: number, y: number, g: Generator, state: GridState): void {
    const size = 14;
    const islanded = state.network && state.islandedBuses?.includes(state.network.genBus.get(g.id) ?? "") === true;
    let color = THEME.dim;
    if (islanded) color = THEME.dim;
    else if (g.status === "on") color = THEME.good;
    else if (g.status === "starting") color = THEME.warn;
    else if (g.status === "stopping") color = THEME.warn;
    else if (g.status === "tripped") color = THEME.danger;

    // Shape varies by type
    ctx.fillStyle = color;
    ctx.beginPath();
    if (g.type === "wind" || g.type === "solar") {
      // Triangle for renewables
      ctx.moveTo(x, y - size);
      ctx.lineTo(x + size * 0.866, y + size * 0.5);
      ctx.lineTo(x - size * 0.866, y + size * 0.5);
    } else if (g.type === "battery") {
      // Square for storage
      ctx.rect(x - size * 0.8, y - size * 0.8, size * 1.6, size * 1.6);
    } else {
      // Diamond for thermal
      ctx.moveTo(x, y - size);
      ctx.lineTo(x + size, y);
      ctx.lineTo(x, y + size);
      ctx.lineTo(x - size, y);
    }
    ctx.closePath();
    ctx.fill();

    if (g.status === "off") {
      // Hollow centre
      ctx.fillStyle = THEME.bg;
      ctx.beginPath();
      if (g.type === "battery") {
        ctx.rect(x - size * 0.4, y - size * 0.4, size * 0.8, size * 0.8);
      } else if (g.type === "wind" || g.type === "solar") {
        ctx.moveTo(x, y - size * 0.5);
        ctx.lineTo(x + size * 0.43, y + size * 0.25);
        ctx.lineTo(x - size * 0.43, y + size * 0.25);
      } else {
        ctx.moveTo(x, y - size / 2);
        ctx.lineTo(x + size / 2, y);
        ctx.lineTo(x, y + size / 2);
        ctx.lineTo(x - size / 2, y);
      }
      ctx.closePath();
      ctx.fill();
    }

    // Label
    ctx.fillStyle = THEME.fg;
    ctx.font = "13px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(g.name, x + size + 8, y - 8);

    ctx.fillStyle = THEME.dim;
    ctx.font = "11px ui-monospace, SFMono-Regular, Menlo, monospace";
    let statusText: string;
    if (g.type === "battery" && g.status === "on") {
      const dir = g.output > 0.5 ? "→" : g.output < -0.5 ? "←" : "·";
      statusText = `${dir} ${Math.abs(Math.round(g.output))} MW`;
    } else if (g.status === "on" && (g.type === "wind" || g.type === "solar")) {
      const avail = g.availability;
      if (avail < 0.05) statusText = g.type === "solar" ? "no sun" : "becalmed";
      else statusText = `${Math.round(g.output)} MW (${Math.round(avail * 100)}%)`;
    } else if (g.status === "on") {
      statusText = `${Math.round(g.output)} / ${Math.round(g.pMax * g.availability)} MW`;
    } else if (g.status === "starting") {
      statusText = `starting ${g.statusTimer.toFixed(0)}m`;
    } else if (g.status === "stopping") {
      statusText = `stopping`;
    } else if (g.status === "tripped") {
      statusText = `tripped`;
    } else {
      statusText = `off`;
    }
    ctx.fillText(statusText, x + size + 8, y + 8);

    // Battery SoC bar
    if (g.type === "battery" && g.storageCapacity) {
      const soc = g.stateOfCharge ?? 0;
      const cap = g.storageCapacity;
      const barW = 60;
      const barX = x + size + 8;
      const barY = y + 18;
      ctx.fillStyle = THEME.grid;
      ctx.fillRect(barX, barY, barW, 4);
      ctx.fillStyle = THEME.accent;
      ctx.fillRect(barX, barY, barW * (soc / cap), 4);
      ctx.fillStyle = THEME.dim;
      ctx.font = "9px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textBaseline = "top";
      ctx.fillText(`${soc.toFixed(0)}/${cap} MWh`, barX, barY + 6);
    }
  }

  private drawLoad(
    ctx: CanvasRenderingContext2D, x: number, y: number,
    name: string, mw: number, isShed: boolean, isIslanded: boolean,
  ): void {
    const r = 12;
    const dark = isShed || isIslanded;
    ctx.fillStyle = dark ? THEME.dim : THEME.accent;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    if (dark) {
      ctx.fillStyle = THEME.bg;
      ctx.beginPath();
      ctx.arc(x, y, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = THEME.fg;
    ctx.font = "13px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillText(name, x + r + 8, y - 8);
    ctx.fillStyle = dark ? THEME.danger : THEME.dim;
    ctx.font = "11px ui-monospace, SFMono-Regular, Menlo, monospace";
    const txt = isShed ? "shed" : isIslanded ? "no power" : `${Math.round(mw)} MW`;
    ctx.fillText(txt, x + r + 8, y + 8);
  }

  private drawFrequencyStrip(ctx: CanvasRenderingContext2D, c: SimConstants, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = "#0a0c10";
    ctx.fillRect(x, y, w, h);

    const fLow = c.tripLow - 0.2;
    const fHigh = c.tripHigh + 0.2;
    const pad = 24;
    const top = y + pad;
    const bot = y + h - pad;
    const left = x + 60;
    const right = x + w - 24;
    const toY = (f: number) => bot - ((f - fLow) / (fHigh - fLow)) * (bot - top);

    ctx.fillStyle = withAlpha(THEME.good, 0.10);
    ctx.fillRect(left, toY(c.bandHigh), right - left, toY(c.bandLow) - toY(c.bandHigh));
    ctx.fillStyle = withAlpha(THEME.warn, 0.05);
    ctx.fillRect(left, toY(c.tripHigh), right - left, toY(c.bandHigh) - toY(c.tripHigh));
    ctx.fillRect(left, toY(c.bandLow), right - left, toY(c.tripLow) - toY(c.bandLow));
    ctx.fillStyle = withAlpha(THEME.danger, 0.07);
    ctx.fillRect(left, top, right - left, toY(c.tripHigh) - top);
    ctx.fillRect(left, toY(c.tripLow), right - left, bot - toY(c.tripLow));

    const lines: Array<[number, string, string]> = [
      [c.nominalFrequency, THEME.dim, "50.0"],
      [c.bandHigh, THEME.dim, c.bandHigh.toFixed(1)],
      [c.bandLow, THEME.dim, c.bandLow.toFixed(1)],
      [c.tripHigh, THEME.danger, c.tripHigh.toFixed(1)],
      [c.tripLow, THEME.danger, c.tripLow.toFixed(1)],
    ];
    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textBaseline = "middle";
    for (const [f, color, label] of lines) {
      const yy = toY(f);
      ctx.strokeStyle = withAlpha(color, 0.4);
      ctx.setLineDash(f === c.nominalFrequency ? [] : [2, 4]);
      ctx.beginPath();
      ctx.moveTo(left, yy);
      ctx.lineTo(right, yy);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.textAlign = "right";
      ctx.fillText(label, left - 6, yy);
    }

    if (this.freqHistory.length > 1) {
      const n = this.freqHistory.length;
      ctx.strokeStyle = THEME.accent;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const px = left + (i / (this.maxHistory - 1)) * (right - left);
        const py = toY(this.freqHistory[i]!);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
      const lastIdx = n - 1;
      const px = left + (lastIdx / (this.maxHistory - 1)) * (right - left);
      const py = toY(this.freqHistory[lastIdx]!);
      ctx.fillStyle = THEME.accent;
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = THEME.dim;
    ctx.font = "11px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("FREQUENCY · Hz", x + 12, y + 8);
  }
}

function withAlpha(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
